import { Check, ChevronDown, EyeOff, ExternalLink } from "lucide-react";
import { NotificationItem } from "@/entities/notification/model/types";
import { NotificationGroup, isChat, relativeTime } from "../lib/notificationGroups";
import { NotificationAvatar } from "./NotificationAvatar";

type Translate = (key: string, opts?: Record<string, unknown>) => string;

interface RowActions {
    onMarkRead: (id: string) => void;
    onHide: (id: string) => void;
    onOpenLink: (item: NotificationItem) => void;
}

function UnreadDot({ visible }: { visible: boolean }) {
    return (
        <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2 ${visible ? "bg-dark_green dark:bg-emerald-400" : "bg-transparent"}`}
            aria-hidden
        />
    );
}

function ActionButtons({
    showMarkRead,
    onMarkRead,
    onHide,
    markReadLabel,
    hideLabel,
}: {
    showMarkRead: boolean;
    onMarkRead: () => void;
    onHide: () => void;
    markReadLabel: string;
    hideLabel: string;
}) {
    return (
        <div className="flex gap-1 self-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-fast">
            {showMarkRead && (
                <button
                    onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
                    className="w-7 h-7 flex items-center justify-center rounded text-zinc-500 dark:text-white/60 hover:bg-zinc-100 dark:hover:bg-white/10 hover:text-dark_green dark:hover:text-emerald-400 transition-colors duration-fast"
                    title={markReadLabel}
                    aria-label={markReadLabel}
                >
                    <Check size={15} />
                </button>
            )}
            <button
                onClick={(e) => { e.stopPropagation(); onHide(); }}
                className="w-7 h-7 flex items-center justify-center rounded text-zinc-500 dark:text-white/60 hover:bg-zinc-100 dark:hover:bg-white/10 hover:text-rose-500 transition-colors duration-fast"
                title={hideLabel}
                aria-label={hideLabel}
            >
                <EyeOff size={15} />
            </button>
        </div>
    );
}

function Timestamp({ item, t }: { item: NotificationItem; t: Translate }) {
    const rel = relativeTime(item.createdAt);
    return (
        <span className="text-[11px] whitespace-nowrap text-zinc-400 dark:text-white/40 flex-shrink-0">
            {t(rel.key, { count: rel.count })}
        </span>
    );
}

export function SingleRow({ item, t, ...actions }: { item: NotificationItem; t: Translate } & RowActions) {
    const hasLink = Boolean(item.link);

    return (
        <div className="group flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors duration-fast">
            <UnreadDot visible={!item.read} />
            <NotificationAvatar item={item} />

            <div
                className={`flex-1 min-w-0 ${hasLink ? "cursor-pointer" : ""}`}
                onClick={hasLink ? () => actions.onOpenLink(item) : undefined}
                role={hasLink ? "link" : undefined}
                title={item.link ?? undefined}
            >
                <div className="flex items-baseline justify-between gap-3">
                    <p className={`truncate text-sm flex items-center gap-1.5 text-zinc-900 dark:text-white ${item.read ? "font-normal" : "font-semibold"}`}>
                        {item.title}
                        {hasLink && <ExternalLink size={12} className="flex-shrink-0 text-zinc-400 dark:text-white/40" />}
                    </p>
                    <Timestamp item={item} t={t} />
                </div>
                <p className="text-xs leading-4 line-clamp-2 mt-0.5 text-zinc-600 dark:text-white/60">{item.message}</p>
            </div>

            <ActionButtons
                showMarkRead={!item.read}
                onMarkRead={() => actions.onMarkRead(item.id)}
                onHide={() => actions.onHide(item.id)}
                markReadLabel={t("nav.notifications.markRead")}
                hideLabel={t("nav.notifications.hide")}
            />
        </div>
    );
}

export function GroupBlock({
    group,
    expanded,
    onToggle,
    onMarkGroupRead,
    onHideGroup,
    t,
    ...actions
}: {
    group: NotificationGroup;
    expanded: boolean;
    onToggle: () => void;
    onMarkGroupRead: () => void;
    onHideGroup: () => void;
    t: Translate;
} & RowActions) {
    const hasUnread = group.unreadCount > 0;
    const countLabel = isChat(group.type)
        ? t("nav.notifications.messagesCount", { count: group.items.length })
        : t("nav.notifications.itemsCount", { count: group.items.length });

    return (
        <div>
            <div className="group flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors duration-fast">
                <UnreadDot visible={hasUnread} />
                <NotificationAvatar item={group.latest} />

                <button onClick={onToggle} className="flex-1 min-w-0 text-left">
                    <div className="flex items-baseline justify-between gap-3">
                        <p className={`truncate text-sm text-zinc-900 dark:text-white ${hasUnread ? "font-semibold" : "font-normal"}`}>
                            {group.title}
                            <span className="ml-2 text-xs font-normal text-zinc-400 dark:text-white/40">{countLabel}</span>
                        </p>
                        <span className="flex items-center gap-1.5 flex-shrink-0">
                            <Timestamp item={group.latest} t={t} />
                            <ChevronDown
                                size={14}
                                className={`text-zinc-400 dark:text-white/40 transition-transform duration-fast ${expanded ? "rotate-180" : ""}`}
                            />
                        </span>
                    </div>
                    <p className="text-xs leading-4 line-clamp-1 mt-0.5 text-zinc-600 dark:text-white/60">{group.latest.message}</p>
                </button>

                <ActionButtons
                    showMarkRead={hasUnread}
                    onMarkRead={onMarkGroupRead}
                    onHide={onHideGroup}
                    markReadLabel={t("nav.notifications.markAllRead")}
                    hideLabel={t("nav.notifications.hideAll")}
                />
            </div>

            {expanded && (
                <div className="pl-[3.25rem] pr-4 pb-2 flex flex-col">
                    {group.items.map((item) => (
                        <div
                            key={item.id}
                            className="group flex items-start gap-3 py-2 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors duration-fast"
                        >
                            <UnreadDot visible={!item.read} />
                            <div
                                className={`flex-1 min-w-0 ${item.link ? "cursor-pointer" : ""}`}
                                onClick={item.link ? () => actions.onOpenLink(item) : undefined}
                                role={item.link ? "link" : undefined}
                                title={item.link ?? undefined}
                            >
                                <p className="text-xs leading-4 line-clamp-2 text-zinc-700 dark:text-white/70 flex items-center gap-1.5">
                                    {item.message}
                                    {item.link && <ExternalLink size={11} className="flex-shrink-0 text-zinc-400 dark:text-white/40" />}
                                </p>
                                <Timestamp item={item} t={t} />
                            </div>
                            <ActionButtons
                                showMarkRead={!item.read}
                                onMarkRead={() => actions.onMarkRead(item.id)}
                                onHide={() => actions.onHide(item.id)}
                                markReadLabel={t("nav.notifications.markRead")}
                                hideLabel={t("nav.notifications.hide")}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
